// src/client/renderer/character/color-swap.ts
//
// Applies a player color to the body mesh's material.
// Hex values are authoritative from docs/design/tokens.json `color.player.*`.

import * as THREE from 'three';
import type { CharacterRig, ColorId } from './types';
import { COLOR_ID_TO_HEX } from './types';

/**
 * Apply player color to the body mesh's material. Pure function — mutates
 * bodyMesh.material's `.color` in place. Idempotent. Material identity is
 * preserved (no clone, no replacement).
 *
 * Works for any THREE.Material subclass that exposes a `.color` property
 * (MeshStandardMaterial, MeshBasicMaterial, MeshLambertMaterial, etc.).
 * Uses a duck-typed `'color' in material` check to detect the property.
 *
 * @throws {Error} If the mesh's material is an array (multi-material mesh)
 *   or if the material does not expose a `.color` property (e.g. ShaderMaterial
 *   without a color uniform). Both cases are unsupported for color swap.
 */
export function applyBodyColor(target: THREE.Mesh | CharacterRig, colorId: ColorId): void {
  if ('colorableMeshes' in target && target.colorableMeshes != null) {
    for (const mesh of target.colorableMeshes) applyBodyColorToMesh(mesh, colorId);
    return;
  }
  if (target instanceof THREE.Mesh) {
    applyBodyColorToMesh(target, colorId);
    return;
  }
  applyBodyColorToMesh(target.bodyMesh, colorId);
}

function applyBodyColorToMesh(bodyMesh: THREE.Mesh, colorId: ColorId): void {
  const material = bodyMesh.material;

  // Multi-material arrays are not supported — body mesh must use a single material.
  if (Array.isArray(material)) {
    throw new Error(
      `[applyBodyColor] bodyMesh.material is an array of ${material.length} materials. ` +
        'Color swap requires a single material. Ensure character-body.glb has a single material slot.',
    );
  }

  // Duck-typed check: does this material subclass expose a `.color` property?
  if (!('color' in material)) {
    throw new Error(
      `[applyBodyColor] bodyMesh.material (${material.type}) does not expose a ".color" property. ` +
        'Color swap requires a material with a .color accessor (e.g. MeshStandardMaterial, ' +
        'MeshBasicMaterial, MeshLambertMaterial). ShaderMaterial without a color uniform is not supported.',
    );
  }

  const hex = COLOR_ID_TO_HEX[colorId];
  // Cast to THREE.Color-bearing material type via interface narrowing.
  (material as unknown as { color: THREE.Color }).color.setHex(hex);
}
